'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ar } from '@daypicker/react/locale';
import {
  AlertTriangle,
  Bell,
  CalendarClock,
  CalendarIcon,
  FileUp,
  LayoutGrid,
  Palette,
  Printer,
  Save,
  Scan,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Wand2
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { usePermissions } from '@/hooks/use-permissions';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { ColorPickerPopover } from '@/components/color-picker-popover';
import { configurationQueries } from '../services/configurations.queries';
import { DEFAULT_OCR_HIGHLIGHT_COLOR } from '../hooks/use-app-settings';
import { useUpdateAppSettingsMutation } from '../services/configurations.mutations';
import type { AppSettings, PrintMode } from '../types';
import { QrSizeCalculatorDialog } from './qr-size-calculator-dialog';

const CONFIG_TABS = ['printing', 'processing', 'general'] as const;

export function ConfigurationsPageContent() {
  const t = useTranslations('configurations');
  const locale = useLocale();
  const { isSuperAdmin, hasPermission } = usePermissions();
  const { data, isLoading } = useQuery(configurationQueries.all());
  const { data: quotaData } = useQuery(configurationQueries.scanPageQuota());
  const updateMutation = useUpdateAppSettingsMutation();

  // Reading this page needs only `configurations.view`; every control on it
  // WRITES, and the backend PUT accepts `SuperAdmin|configurations.edit`.
  // `hasPermission` already returns true for a Super Admin, so this mirrors
  // that middleware exactly — a view-only account sees the settings and
  // cannot arm a control that would 403.
  const canEditConfigurations = hasPermission('configurations.edit');
  const writeDisabled = updateMutation.isPending || !canEditConfigurations;

  const settings: AppSettings = (data as any)?.data ?? data ?? {};
  const quota = ((quotaData as any)?.data ?? quotaData) as
    { used: number; limit: number; unlimited: boolean } | undefined;

  // Active tab lives in the URL (`?tab=`) so it's deep-linkable and survives
  // refresh / browser Back. `parseAsStringLiteral` ignores unknown values,
  // falling back to `printing`.
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsStringLiteral(CONFIG_TABS).withDefault('printing')
  );

  // Container-creation cutoff (an ISO `YYYY-MM-DD`; empty = no limit). Local
  // state so the picker updates instantly; re-synced when settings load/change.
  const savedContainerExpiry =
    (settings.container_creation_expires_at as string) ?? '';
  const [containerExpiry, setContainerExpiry] = useState<string>('');
  useEffect(() => {
    setContainerExpiry(savedContainerExpiry);
  }, [savedContainerExpiry]);

  // Default OCR highlight color. Local state so the wheel previews instantly
  // (a drag fires many changes); persisted only on Save.
  const savedHighlightColor =
    (settings.ocr_default_highlight_color as string) ??
    DEFAULT_OCR_HIGHLIGHT_COLOR;
  const [highlightColor, setHighlightColor] = useState<string>(
    DEFAULT_OCR_HIGHLIGHT_COLOR
  );
  useEffect(() => {
    setHighlightColor(savedHighlightColor);
  }, [savedHighlightColor]);

  const widthRef = useRef<HTMLInputElement>(null);
  const heightRef = useRef<HTMLInputElement>(null);
  const gapRef = useRef<HTMLInputElement>(null);
  const dpiRef = useRef<HTMLInputElement>(null);
  const qrHeaderGapRef = useRef<HTMLInputElement>(null);
  const qrFooterGapRef = useRef<HTMLInputElement>(null);
  const qrHeaderFontRef = useRef<HTMLInputElement>(null);
  const qrFooterFontRef = useRef<HTMLInputElement>(null);

  const paperWRef = useRef<HTMLInputElement>(null);
  const paperHRef = useRef<HTMLInputElement>(null);
  const colsRef = useRef<HTMLInputElement>(null);
  const rowsRef = useRef<HTMLInputElement>(null);
  const marginRef = useRef<HTMLInputElement>(null);

  const continuousColsRef = useRef<HTMLInputElement>(null);
  const continuousMarginRef = useRef<HTMLInputElement>(null);
  const continuousRollRef = useRef<HTMLInputElement>(null);

  const rejectionThresholdRef = useRef<HTMLInputElement>(null);
  const maxScanPagesRef = useRef<HTMLInputElement>(null);

  // Live state for the "physical roll width" input so the mismatch warning
  // updates as the user types, before they hit Save. Synced with the
  // saved value when the settings cache changes.
  const savedPhysicalRoll = Number(
    settings.continuous_physical_roll_width_mm ?? 0
  );
  const [physicalRollMm, setPhysicalRollMm] =
    useState<number>(savedPhysicalRoll);
  useEffect(() => {
    setPhysicalRollMm(savedPhysicalRoll);
  }, [savedPhysicalRoll]);

  const savedMode: PrintMode =
    settings.print_mode === 'grid'
      ? 'grid'
      : settings.print_mode === 'continuous'
        ? 'continuous'
        : 'sticker';
  const [printMode, setPrintMode] = useState<PrintMode>(savedMode);
  // Keep local mode in sync if the cached settings change after save.
  useEffect(() => {
    setPrintMode(savedMode);
  }, [savedMode]);

  function handleLabelPrintingSave() {
    const width = parseFloat(widthRef.current?.value ?? '');
    const height = parseFloat(heightRef.current?.value ?? '');
    const gap = parseFloat(gapRef.current?.value ?? '');
    const dpi = parseInt(dpiRef.current?.value ?? '', 10);
    const qrHeaderGap = parseFloat(qrHeaderGapRef.current?.value ?? '');
    const qrFooterGap = parseFloat(qrFooterGapRef.current?.value ?? '');
    const qrHeaderFont = parseFloat(qrHeaderFontRef.current?.value ?? '');
    const qrFooterFont = parseFloat(qrFooterFontRef.current?.value ?? '');
    if (isNaN(width) || isNaN(height) || isNaN(gap) || isNaN(dpi)) return;
    updateMutation.mutate({
      settings: {
        label_width_mm: width,
        label_height_mm: height,
        label_gap_mm: gap,
        label_dpi: dpi,
        // Default to 0.5 mm if the operator clears either gap field —
        // matches the hook default and keeps the visible gap close to
        // the legacy computed margin.
        qr_header_gap_mm: Number.isFinite(qrHeaderGap)
          ? Math.max(0, qrHeaderGap)
          : 0.5,
        qr_footer_gap_mm: Number.isFinite(qrFooterGap)
          ? Math.max(0, qrFooterGap)
          : 0.5,
        // 0 = auto-fit (let the ZPL solver pick the largest font that
        // fits the QR width). Negative or empty inputs are clamped to 0.
        qr_header_font_mm: Number.isFinite(qrHeaderFont)
          ? Math.max(0, qrHeaderFont)
          : 0,
        qr_footer_font_mm: Number.isFinite(qrFooterFont)
          ? Math.max(0, qrFooterFont)
          : 0
      }
    });
  }

  function handlePrintModeSave() {
    const payload: Record<string, unknown> = { print_mode: printMode };
    if (printMode === 'grid') {
      const paperW = parseFloat(paperWRef.current?.value ?? '');
      const paperH = parseFloat(paperHRef.current?.value ?? '');
      const cols = parseInt(colsRef.current?.value ?? '', 10);
      const rows = parseInt(rowsRef.current?.value ?? '', 10);
      const margin = parseFloat(marginRef.current?.value ?? '');
      if (
        isNaN(paperW) ||
        isNaN(paperH) ||
        isNaN(cols) ||
        isNaN(rows) ||
        isNaN(margin)
      ) {
        return;
      }
      payload.paper_width_mm = paperW;
      payload.paper_height_mm = paperH;
      payload.grid_cols = cols;
      payload.grid_rows = rows;
      payload.grid_margin_mm = margin;
    } else if (printMode === 'continuous') {
      const cols = parseInt(continuousColsRef.current?.value ?? '', 10);
      const margin = parseFloat(continuousMarginRef.current?.value ?? '');
      const roll = parseFloat(continuousRollRef.current?.value ?? '');
      if (isNaN(cols) || isNaN(margin)) {
        return;
      }
      payload.continuous_columns = Math.max(1, cols);
      payload.continuous_edge_margin_mm = Math.max(0, margin);
      // Empty / NaN means the operator hasn't measured the roll yet — store
      // 0 to keep the warning silent rather than persisting a stale value.
      payload.continuous_physical_roll_width_mm = Number.isFinite(roll)
        ? Math.max(0, roll)
        : 0;
    }
    updateMutation.mutate({ settings: payload as Partial<AppSettings> });
  }

  // "Apply suggested label width" — push label_width_mm and label_height_mm
  // to the value that makes 6 cells × pitch fill the user's measured roll.
  // Square cells because every Zebra die-cut sticker we ship is square.
  function handleApplySuggestedLabelWidth(suggestedMm: number) {
    const rounded = Math.round(suggestedMm * 1000) / 1000;
    updateMutation.mutate({
      settings: {
        label_width_mm: rounded,
        label_height_mm: rounded
      }
    });
  }

  function handleQa2OcrToggle(value: boolean) {
    updateMutation.mutate({ settings: { enable_qa2_ocr: value } });
  }

  function handleHighlightColorSave() {
    updateMutation.mutate({
      settings: { ocr_default_highlight_color: highlightColor }
    });
  }

  function handleUseHardwareScannerToggle(value: boolean) {
    updateMutation.mutate({ settings: { use_hardware_scanner: value } });
  }

  function handleNotificationsToggle(value: boolean) {
    updateMutation.mutate({ settings: { notifications_enabled: value } });
  }

  function handleRejectionThresholdSave() {
    const raw = rejectionThresholdRef.current?.value ?? '';
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < 0) return;
    updateMutation.mutate({
      settings: { rejection_alert_threshold: parsed }
    });
  }

  function handleScanPageLimitSave() {
    const parsed = parseInt(maxScanPagesRef.current?.value ?? '', 10);
    if (isNaN(parsed) || parsed < 0) return;
    updateMutation.mutate({
      settings: { max_scan_pages: parsed }
    });
  }

  function handleContainerExpirySave() {
    updateMutation.mutate({
      settings: { container_creation_expires_at: containerExpiry }
    });
  }

  function handleContainerExpiryClear() {
    setContainerExpiry('');
    updateMutation.mutate({
      settings: { container_creation_expires_at: '' }
    });
  }

  function handleDynamicFormLabModeChange(value: 'single' | 'multi') {
    updateMutation.mutate({ settings: { dynamic_form_lab_mode: value } });
  }

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <Skeleton className='h-8 w-48' />
        <Skeleton className='h-64 w-full' />
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>{t('pageTitle')}</h1>
        <p className='text-muted-foreground text-sm'>{t('pageDescription')}</p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          void setActiveTab(v as (typeof CONFIG_TABS)[number])
        }
        className='w-full'
      >
        <TabsList className='h-10'>
          <TabsTrigger value='printing'>
            <Printer className='size-4' />
            {t('tabPrinting')}
          </TabsTrigger>
          <TabsTrigger value='processing'>
            <ShieldCheck className='size-4' />
            {t('tabProcessing')}
          </TabsTrigger>
          <TabsTrigger value='general'>
            <Settings2 className='size-4' />
            {t('tabGeneral')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value='printing' className='mt-4 space-y-6'>
          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <Printer className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>
                  {t('labelPrinting')}
                </CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('labelPrintingDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='label_width_mm'
                    className='text-sm font-medium'
                  >
                    {t('labelWidthMm')}
                  </Label>
                  <Input
                    ref={widthRef}
                    id='label_width_mm'
                    type='number'
                    step='0.001'
                    min='0.1'
                    defaultValue={String(settings.label_width_mm ?? '')}
                    key={String(settings.label_width_mm)}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='label_height_mm'
                    className='text-sm font-medium'
                  >
                    {t('labelHeightMm')}
                  </Label>
                  <Input
                    ref={heightRef}
                    id='label_height_mm'
                    type='number'
                    step='0.001'
                    min='0.1'
                    defaultValue={String(settings.label_height_mm ?? '')}
                    key={String(settings.label_height_mm)}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label htmlFor='label_gap_mm' className='text-sm font-medium'>
                    {t('labelGapMm')}
                  </Label>
                  <Input
                    ref={gapRef}
                    id='label_gap_mm'
                    type='number'
                    step='0.001'
                    min='0.1'
                    defaultValue={String(settings.label_gap_mm ?? '')}
                    key={String(settings.label_gap_mm)}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                </div>
                <div className='space-y-1.5'>
                  <Label htmlFor='label_dpi' className='text-sm font-medium'>
                    {t('labelDpi')}
                  </Label>
                  <Input
                    ref={dpiRef}
                    id='label_dpi'
                    type='number'
                    step='1'
                    min='100'
                    defaultValue={String(settings.label_dpi ?? 203)}
                    key={String(settings.label_dpi)}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                </div>
              </div>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='qr_header_gap_mm'
                    className='text-sm font-medium'
                  >
                    {t('qrHeaderGapMm')}
                  </Label>
                  <Input
                    ref={qrHeaderGapRef}
                    id='qr_header_gap_mm'
                    type='number'
                    step='0.1'
                    min='0'
                    defaultValue={String(settings.qr_header_gap_mm ?? 0.5)}
                    key={`qr_header_gap_${String(settings.qr_header_gap_mm)}`}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                  <p className='text-muted-foreground text-xs'>
                    {t('qrHeaderGapMmHelp')}
                  </p>
                </div>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='qr_footer_gap_mm'
                    className='text-sm font-medium'
                  >
                    {t('qrFooterGapMm')}
                  </Label>
                  <Input
                    ref={qrFooterGapRef}
                    id='qr_footer_gap_mm'
                    type='number'
                    step='0.1'
                    min='0'
                    defaultValue={String(settings.qr_footer_gap_mm ?? 0.5)}
                    key={`qr_footer_gap_${String(settings.qr_footer_gap_mm)}`}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                  <p className='text-muted-foreground text-xs'>
                    {t('qrFooterGapMmHelp')}
                  </p>
                </div>
              </div>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='qr_header_font_mm'
                    className='text-sm font-medium'
                  >
                    {t('qrHeaderFontMm')}
                  </Label>
                  <Input
                    ref={qrHeaderFontRef}
                    id='qr_header_font_mm'
                    type='number'
                    step='0.1'
                    min='0'
                    defaultValue={String(settings.qr_header_font_mm ?? 0)}
                    key={`qr_header_font_${String(settings.qr_header_font_mm)}`}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                  <p className='text-muted-foreground text-xs'>
                    {t('qrHeaderFontMmHelp')}
                  </p>
                </div>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='qr_footer_font_mm'
                    className='text-sm font-medium'
                  >
                    {t('qrFooterFontMm')}
                  </Label>
                  <Input
                    ref={qrFooterFontRef}
                    id='qr_footer_font_mm'
                    type='number'
                    step='0.1'
                    min='0'
                    defaultValue={String(settings.qr_footer_font_mm ?? 0)}
                    key={`qr_footer_font_${String(settings.qr_footer_font_mm)}`}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                  <p className='text-muted-foreground text-xs'>
                    {t('qrFooterFontMmHelp')}
                  </p>
                </div>
              </div>
              <div className='flex items-center justify-end gap-2'>
                <QrSizeCalculatorDialog
                  initialPaperWidthMm={Number(settings.label_width_mm) || 75}
                  initialPaperHeightMm={Number(settings.label_height_mm) || 100}
                />
                <Button
                  size='sm'
                  onClick={handleLabelPrintingSave}
                  disabled={writeDisabled}
                >
                  <Save className='me-2 h-4 w-4' />
                  {t('save')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <LayoutGrid className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>{t('printMode')}</CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('printModeDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <RadioGroup
                value={printMode}
                onValueChange={(v) => setPrintMode(v as PrintMode)}
                className='grid grid-cols-1 gap-3 sm:grid-cols-3'
              >
                <label
                  htmlFor='print-mode-sticker'
                  className='hover:bg-muted/40 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors'
                >
                  <RadioGroupItem
                    id='print-mode-sticker'
                    value='sticker'
                    className='mt-0.5'
                  />
                  <div className='space-y-1'>
                    <div className='text-sm font-medium'>
                      {t('printModeSticker')}
                    </div>
                    <p className='text-muted-foreground text-xs'>
                      {t('printModeStickerDescription')}
                    </p>
                  </div>
                </label>
                <label
                  htmlFor='print-mode-continuous'
                  className='hover:bg-muted/40 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors'
                >
                  <RadioGroupItem
                    id='print-mode-continuous'
                    value='continuous'
                    className='mt-0.5'
                  />
                  <div className='space-y-1'>
                    <div className='text-sm font-medium'>
                      {t('printModeContinuous')}
                    </div>
                    <p className='text-muted-foreground text-xs'>
                      {t('printModeContinuousDescription')}
                    </p>
                  </div>
                </label>
                <label
                  htmlFor='print-mode-grid'
                  className='hover:bg-muted/40 has-[:checked]:border-primary has-[:checked]:bg-primary/5 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors'
                >
                  <RadioGroupItem
                    id='print-mode-grid'
                    value='grid'
                    className='mt-0.5'
                  />
                  <div className='space-y-1'>
                    <div className='text-sm font-medium'>
                      {t('printModeGrid')}
                    </div>
                    <p className='text-muted-foreground text-xs'>
                      {t('printModeGridDescription')}
                    </p>
                  </div>
                </label>
              </RadioGroup>

              {printMode === 'grid' && (
                <>
                  <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-5'>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='paper_width_mm'
                        className='text-sm font-medium'
                      >
                        {t('paperWidthMm')}
                      </Label>
                      <Input
                        ref={paperWRef}
                        id='paper_width_mm'
                        type='number'
                        step='0.1'
                        min='1'
                        defaultValue={String(settings.paper_width_mm ?? 100)}
                        key={`paper_w_${String(settings.paper_width_mm)}`}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                    </div>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='paper_height_mm'
                        className='text-sm font-medium'
                      >
                        {t('paperHeightMm')}
                      </Label>
                      <Input
                        ref={paperHRef}
                        id='paper_height_mm'
                        type='number'
                        step='0.1'
                        min='1'
                        defaultValue={String(settings.paper_height_mm ?? 75)}
                        key={`paper_h_${String(settings.paper_height_mm)}`}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                    </div>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='grid_cols'
                        className='text-sm font-medium'
                      >
                        {t('gridCols')}
                      </Label>
                      <Input
                        ref={colsRef}
                        id='grid_cols'
                        type='number'
                        step='1'
                        min='1'
                        defaultValue={String(settings.grid_cols ?? 3)}
                        key={`cols_${String(settings.grid_cols)}`}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                    </div>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='grid_rows'
                        className='text-sm font-medium'
                      >
                        {t('gridRows')}
                      </Label>
                      <Input
                        ref={rowsRef}
                        id='grid_rows'
                        type='number'
                        step='1'
                        min='1'
                        defaultValue={String(settings.grid_rows ?? 3)}
                        key={`rows_${String(settings.grid_rows)}`}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                    </div>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='grid_margin_mm'
                        className='text-sm font-medium'
                      >
                        {t('gridMarginMm')}
                      </Label>
                      <Input
                        ref={marginRef}
                        id='grid_margin_mm'
                        type='number'
                        step='0.1'
                        min='0'
                        defaultValue={String(settings.grid_margin_mm ?? 2)}
                        key={`margin_${String(settings.grid_margin_mm)}`}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                    </div>
                  </div>
                  <p className='text-muted-foreground text-xs'>
                    {t('gridGeometryHint', {
                      cols: Number(settings.grid_cols ?? 3),
                      rows: Number(settings.grid_rows ?? 3),
                      total:
                        Number(settings.grid_cols ?? 3) *
                        Number(settings.grid_rows ?? 3)
                    })}
                  </p>
                </>
              )}

              {printMode === 'continuous' && (
                <>
                  <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3'>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='continuous_columns'
                        className='text-sm font-medium'
                      >
                        {t('continuousColumns')}
                      </Label>
                      <Input
                        ref={continuousColsRef}
                        id='continuous_columns'
                        type='number'
                        step='1'
                        min='1'
                        defaultValue={String(settings.continuous_columns ?? 1)}
                        key={`continuous_cols_${String(settings.continuous_columns)}`}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                    </div>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='continuous_edge_margin_mm'
                        className='text-sm font-medium'
                      >
                        {t('continuousEdgeMarginMm')}
                      </Label>
                      <Input
                        ref={continuousMarginRef}
                        id='continuous_edge_margin_mm'
                        type='number'
                        step='0.1'
                        min='0'
                        defaultValue={String(
                          settings.continuous_edge_margin_mm ?? 0
                        )}
                        key={`continuous_margin_${String(settings.continuous_edge_margin_mm)}`}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                    </div>
                    <div className='space-y-1.5'>
                      <Label
                        htmlFor='continuous_physical_roll_width_mm'
                        className='text-sm font-medium'
                      >
                        {t('physicalRollWidthMm')}
                      </Label>
                      <Input
                        ref={continuousRollRef}
                        id='continuous_physical_roll_width_mm'
                        type='number'
                        step='0.1'
                        min='0'
                        placeholder={t('physicalRollWidthPlaceholder')}
                        defaultValue={
                          savedPhysicalRoll > 0 ? String(savedPhysicalRoll) : ''
                        }
                        key={`continuous_roll_${String(savedPhysicalRoll)}`}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value);
                          setPhysicalRollMm(
                            Number.isFinite(v) ? Math.max(0, v) : 0
                          );
                        }}
                        disabled={writeDisabled}
                        className='h-9 text-sm'
                      />
                      <p className='text-muted-foreground text-xs'>
                        {t('physicalRollWidthHelp')}
                      </p>
                    </div>
                  </div>
                  <p className='text-muted-foreground text-xs'>
                    {t('continuousGeometryHint', {
                      cols: Number(settings.continuous_columns ?? 1),
                      width: (
                        Number(settings.continuous_columns ?? 1) *
                          Number(settings.label_width_mm ?? 12.7) +
                        (Number(settings.continuous_columns ?? 1) - 1) *
                          Number(settings.label_gap_mm ?? 3.175) +
                        2 * Number(settings.continuous_edge_margin_mm ?? 0)
                      ).toFixed(2)
                    })}
                  </p>

                  {(() => {
                    // Roll-width mismatch warning. Compares the saved configured
                    // paper width against the operator-measured physical roll
                    // and offers a one-click fix that sets label_width_mm /
                    // label_height_mm to the pitch the printer's gap sensor
                    // actually advances. 0.5 mm tolerance covers calculator
                    // rounding. We compute against *saved* cols/edge-margin
                    // (matching the geometry hint above); if the user has
                    // unsaved edits to those fields they should hit Save first.
                    const cols = Number(settings.continuous_columns ?? 1);
                    const labelWidthMm = Number(
                      settings.label_width_mm ?? 12.7
                    );
                    const gapMm = Number(settings.label_gap_mm ?? 0);
                    const edgeMarginMm = Number(
                      settings.continuous_edge_margin_mm ?? 0
                    );
                    const computedPaperWidth =
                      cols * labelWidthMm +
                      (cols - 1) * gapMm +
                      2 * edgeMarginMm;
                    const tolerance = 0.5;
                    const mismatch =
                      physicalRollMm > 0 &&
                      cols > 0 &&
                      Math.abs(computedPaperWidth - physicalRollMm) > tolerance;
                    if (!mismatch) return null;
                    const suggested =
                      (physicalRollMm - 2 * edgeMarginMm - (cols - 1) * gapMm) /
                      cols;
                    return (
                      <Alert variant='destructive'>
                        <AlertTriangle />
                        <AlertTitle>{t('rollWidthMismatchTitle')}</AlertTitle>
                        <AlertDescription>
                          <p>
                            {t('rollWidthMismatchBody', {
                              computed: computedPaperWidth.toFixed(2),
                              physical: physicalRollMm.toFixed(2),
                              suggested: suggested.toFixed(3)
                            })}
                          </p>
                          <Button
                            type='button'
                            size='sm'
                            variant='outline'
                            className='mt-2'
                            onClick={() =>
                              handleApplySuggestedLabelWidth(suggested)
                            }
                            disabled={writeDisabled || suggested <= 0}
                          >
                            <Wand2 className='me-2 h-4 w-4' />
                            {t('applySuggestedLabelWidth', {
                              value: suggested.toFixed(3)
                            })}
                          </Button>
                        </AlertDescription>
                      </Alert>
                    );
                  })()}
                </>
              )}

              <div className='flex items-center justify-end gap-2'>
                <Button
                  size='sm'
                  onClick={handlePrintModeSave}
                  disabled={writeDisabled}
                >
                  <Save className='me-2 h-4 w-4' />
                  {t('save')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='processing' className='mt-4 space-y-6'>
          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <ShieldCheck className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>{t('ocrQaSection')}</CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('ocrQaSectionDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className='flex items-center justify-between gap-4'>
                <div className='space-y-0.5'>
                  <Label
                    htmlFor='enable_qa2_ocr'
                    className='text-sm font-medium'
                  >
                    {t('enableQa2OcrLabel')}
                  </Label>
                  <p className='text-muted-foreground text-xs'>
                    {t('enableQa2OcrDescription')}
                  </p>
                </div>
                <Switch
                  id='enable_qa2_ocr'
                  checked={Boolean(settings.enable_qa2_ocr)}
                  onCheckedChange={handleQa2OcrToggle}
                  disabled={writeDisabled}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <Palette className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>
                  {t('ocrHighlightSection')}
                </CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('ocrHighlightSectionDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-1.5'>
                  <Label className='text-sm font-medium'>
                    {t('ocrDefaultHighlightColor')}
                  </Label>
                  <div className='flex items-center gap-2'>
                    <ColorPickerPopover
                      value={highlightColor}
                      onChange={setHighlightColor}
                      onReset={() =>
                        setHighlightColor(DEFAULT_OCR_HIGHLIGHT_COLOR)
                      }
                      resetLabel={t('ocrDefaultHighlightColorReset')}
                      label={highlightColor.toUpperCase()}
                      disabled={writeDisabled}
                      className='w-40'
                    />
                    <span
                      className='h-9 flex-1 rounded-md border'
                      style={{ backgroundColor: highlightColor }}
                      aria-hidden='true'
                    />
                  </div>
                  <p className='text-muted-foreground text-xs'>
                    {t('ocrDefaultHighlightColorHelp')}
                  </p>
                </div>
              </div>
              <div className='flex items-center justify-end gap-2'>
                <Button
                  size='sm'
                  onClick={handleHighlightColorSave}
                  disabled={
                    writeDisabled || highlightColor === savedHighlightColor
                  }
                >
                  <Save className='me-2 h-4 w-4' />
                  {t('save')}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <ShieldAlert className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>
                  {t('reviewSection')}
                </CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('reviewSectionDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-1.5'>
                  <Label
                    htmlFor='rejection_alert_threshold'
                    className='text-sm font-medium'
                  >
                    {t('rejectionAlertThreshold')}
                  </Label>
                  <Input
                    ref={rejectionThresholdRef}
                    id='rejection_alert_threshold'
                    type='number'
                    step='1'
                    min='0'
                    defaultValue={String(
                      settings.rejection_alert_threshold ?? 3
                    )}
                    key={`rejection_threshold_${String(settings.rejection_alert_threshold)}`}
                    disabled={writeDisabled}
                    className='h-9 text-sm'
                  />
                  <p className='text-muted-foreground text-xs'>
                    {t('rejectionAlertThresholdHelp')}
                  </p>
                </div>
              </div>
              <div className='flex items-center justify-end gap-2'>
                <Button
                  size='sm'
                  onClick={handleRejectionThresholdSave}
                  disabled={writeDisabled}
                >
                  <Save className='me-2 h-4 w-4' />
                  {t('save')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value='general' className='mt-4 space-y-6'>
          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <Scan className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>
                  {t('captureModeSection')}
                </CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('captureModeSectionDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className='flex items-center justify-between gap-4'>
                <div className='space-y-0.5'>
                  <Label
                    htmlFor='use_hardware_scanner'
                    className='text-sm font-medium'
                  >
                    {t('useHardwareScannerLabel')}
                  </Label>
                  <p className='text-muted-foreground text-xs'>
                    {t('useHardwareScannerDescription')}
                  </p>
                </div>
                <Switch
                  id='use_hardware_scanner'
                  checked={Boolean(settings.use_hardware_scanner ?? true)}
                  onCheckedChange={handleUseHardwareScannerToggle}
                  disabled={writeDisabled}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <Bell className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>
                  {t('notificationsSection')}
                </CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('notificationsSectionDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className='flex items-center justify-between gap-4'>
                <div className='space-y-0.5'>
                  <Label
                    htmlFor='notifications_enabled'
                    className='text-sm font-medium'
                  >
                    {t('notificationsEnabledLabel')}
                  </Label>
                  <p className='text-muted-foreground text-xs'>
                    {t('notificationsEnabledDescription')}
                  </p>
                </div>
                <Switch
                  id='notifications_enabled'
                  checked={Boolean(settings.notifications_enabled ?? true)}
                  onCheckedChange={handleNotificationsToggle}
                  disabled={writeDisabled}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <FileUp className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>
                  {t('scanPageLimitSection')}
                </CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('scanPageLimitSectionDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent className='space-y-4'>
              <div className='space-y-1.5 sm:max-w-xs'>
                <Label htmlFor='max_scan_pages' className='text-sm font-medium'>
                  {t('maxScanPages')}
                </Label>
                <Input
                  ref={maxScanPagesRef}
                  id='max_scan_pages'
                  type='number'
                  step='1'
                  min='0'
                  defaultValue={String(settings.max_scan_pages ?? 0)}
                  key={`max_scan_pages_${String(settings.max_scan_pages)}`}
                  disabled={writeDisabled}
                  className='h-9 text-sm'
                />
                <p className='text-muted-foreground text-xs'>
                  {t('maxScanPagesHelp')}
                </p>
              </div>
              {quota && !quota.unlimited ? (
                <div className='space-y-1.5'>
                  <div className='flex items-center justify-between text-xs'>
                    <span className='text-muted-foreground'>
                      {t('scanPageUsage')}
                    </span>
                    <span
                      className={cn(
                        'font-medium tabular-nums',
                        quota.used >= quota.limit && 'text-destructive'
                      )}
                    >
                      {quota.used} / {quota.limit}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(
                      100,
                      quota.limit > 0 ? (quota.used / quota.limit) * 100 : 0
                    )}
                    className={cn(
                      quota.used >= quota.limit &&
                        '[&>[data-slot=progress-indicator]]:bg-destructive',
                      quota.used < quota.limit &&
                        quota.used / quota.limit >= 0.8 &&
                        '[&>[data-slot=progress-indicator]]:bg-amber-500'
                    )}
                  />
                  {quota.used >= quota.limit ? (
                    <p className='text-destructive text-xs'>
                      {t('scanPageLimitReachedHint')}
                    </p>
                  ) : null}
                </div>
              ) : quota && quota.unlimited ? (
                <p className='text-muted-foreground text-xs'>
                  {t('scanPageUsageUnlimited', { used: quota.used })}
                </p>
              ) : null}
              <div className='flex items-center justify-end gap-2'>
                <Button
                  size='sm'
                  onClick={handleScanPageLimitSave}
                  disabled={writeDisabled}
                >
                  <Save className='me-2 h-4 w-4' />
                  {t('save')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* License card intentionally hidden — feature on hold (client does
              not need signed-license enforcement right now). The `license`
              feature module and the /license-expired page remain intact; to
              re-enable, restore the LicenseKeyForm card here. */}
          {isSuperAdmin && (
            <Card>
              <CardHeader className='flex flex-row items-center gap-2 pb-4'>
                <CalendarClock className='text-primary h-5 w-5' />
                <div>
                  <CardTitle className='text-base'>
                    {t('containerCreationPeriodSection')}
                  </CardTitle>
                  <p className='text-muted-foreground mt-0.5 text-xs'>
                    {t('containerCreationPeriodDescription')}
                  </p>
                </div>
              </CardHeader>
              <CardContent className='space-y-4'>
                {settings.container_creation_expired ? (
                  <p className='text-destructive text-xs font-medium'>
                    {t('containerCreationExpiredNotice')}
                  </p>
                ) : null}
                <div className='flex flex-wrap items-end gap-2'>
                  <div className='space-y-1.5'>
                    <Label className='text-sm font-medium'>
                      {t('containerCreationExpiresAt')}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          disabled={writeDisabled}
                          className={cn(
                            'h-9 w-56 justify-start text-start text-sm font-normal',
                            !containerExpiry && 'text-muted-foreground'
                          )}
                        >
                          {containerExpiry
                            ? format(new Date(containerExpiry), 'PPP')
                            : t('pickDate')}
                          <CalendarIcon className='ms-auto h-4 w-4 opacity-50' />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className='w-auto p-0' align='start'>
                        <Calendar
                          mode='single'
                          locale={locale === 'ar' ? ar : undefined}
                          dir={locale === 'ar' ? 'rtl' : 'ltr'}
                          selected={
                            containerExpiry
                              ? new Date(containerExpiry)
                              : undefined
                          }
                          onSelect={(date) =>
                            setContainerExpiry(
                              date
                                ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
                                : ''
                            )
                          }
                          autoFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <Button
                    size='sm'
                    onClick={handleContainerExpirySave}
                    disabled={writeDisabled}
                  >
                    <Save className='me-2 h-4 w-4' />
                    {t('save')}
                  </Button>
                  {containerExpiry ? (
                    <Button
                      size='sm'
                      variant='ghost'
                      onClick={handleContainerExpiryClear}
                      disabled={writeDisabled}
                    >
                      {t('clear')}
                    </Button>
                  ) : null}
                </div>
                <p className='text-muted-foreground text-xs'>
                  {t('containerCreationExpiresAtHelp')}
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className='flex flex-row items-center gap-2 pb-4'>
              <LayoutGrid className='text-primary h-5 w-5' />
              <div>
                <CardTitle className='text-base'>
                  {t('dynamicFormLabModeSection')}
                </CardTitle>
                <p className='text-muted-foreground mt-0.5 text-xs'>
                  {t('dynamicFormLabModeDescription')}
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={
                  settings.dynamic_form_lab_mode === 'multi'
                    ? 'multi'
                    : 'single'
                }
                onValueChange={(v) =>
                  handleDynamicFormLabModeChange(v as 'single' | 'multi')
                }
                disabled={writeDisabled}
                className='space-y-3'
              >
                <div className='flex items-start gap-3'>
                  <RadioGroupItem
                    value='single'
                    id='dynamic_form_lab_mode_single'
                    className='mt-1'
                  />
                  <div className='space-y-0.5'>
                    <Label
                      htmlFor='dynamic_form_lab_mode_single'
                      className='text-sm font-medium'
                    >
                      {t('dynamicFormLabModeSingle')}
                    </Label>
                    <p className='text-muted-foreground text-xs'>
                      {t('dynamicFormLabModeSingleHint')}
                    </p>
                  </div>
                </div>
                <div className='flex items-start gap-3'>
                  <RadioGroupItem
                    value='multi'
                    id='dynamic_form_lab_mode_multi'
                    className='mt-1'
                  />
                  <div className='space-y-0.5'>
                    <Label
                      htmlFor='dynamic_form_lab_mode_multi'
                      className='text-sm font-medium'
                    >
                      {t('dynamicFormLabModeMulti')}
                    </Label>
                    <p className='text-muted-foreground text-xs'>
                      {t('dynamicFormLabModeMultiHint')}
                    </p>
                  </div>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
